import React, { useState } from 'react';
import { Loader2, Sparkles, Volume2 } from 'lucide-react';

type TTSProvider = 'falai' | 'elevenlabs';

interface Voice {
  id: string;
  name: string;
  provider: TTSProvider;
}

const FAL_VOICES: Voice[] = [
  { id: 'default', name: 'Default', provider: 'falai' },
];

const ELEVENLABS_VOICES: Voice[] = [
  { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George (Warm)', provider: 'elevenlabs' },
  { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel (Calm)', provider: 'elevenlabs' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella (Soft)', provider: 'elevenlabs' },
  { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni (Professional)', provider: 'elevenlabs' },
  { id: 'MF3mGyEYCl7XYWbV9V6O', name: 'Elli (Friendly)', provider: 'elevenlabs' },
  { id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh (Deep)', provider: 'elevenlabs' },
];

interface ScriptToSpeechProps {
  onGenerate: (params: {
    text: string;
    provider: TTSProvider;
    voice_id?: string;
  }) => Promise<void>;
  disabled?: boolean;
  availableProviders?: TTSProvider[];
}

export function ScriptToSpeech({
  onGenerate,
  disabled = false,
  availableProviders = ['falai'],
}: ScriptToSpeechProps) {
  const [text, setText] = useState('');
  const [provider, setProvider] = useState<TTSProvider>(availableProviders[0] || 'falai');
  const [voiceId, setVoiceId] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const voices = provider === 'elevenlabs' ? ELEVENLABS_VOICES : FAL_VOICES;
  const charCount = text.length;
  const maxChars = 5000;

  const handleGenerate = async () => {
    if (!text.trim() || generating) return;
    setError(null);
    setGenerating(true);

    try {
      await onGenerate({
        text: text.trim(),
        provider,
        voice_id: voiceId || undefined,
      });
      setText('');
    } catch (err: any) {
      setError(err.message || 'Failed to generate voiceover');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-amber-400" />
        <span className="text-xs font-medium text-white">AI Script-to-Speech</span>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, maxChars))}
        placeholder="Type your voiceover script here... e.g. 'Welcome to this stunning 3-bedroom property located in the heart of the city...'"
        className="w-full h-24 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white placeholder-slate-600 resize-none focus:outline-none focus:border-purple-500 transition-colors"
        disabled={disabled || generating}
      />

      <div className="flex items-center justify-between">
        <span className={`text-[10px] ${charCount > maxChars * 0.9 ? 'text-amber-400' : 'text-slate-500'}`}>
          {charCount.toLocaleString()} / {maxChars.toLocaleString()} chars
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {availableProviders.length > 1 && (
          <select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value as TTSProvider);
              setVoiceId('');
            }}
            className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-purple-500"
            disabled={generating}
          >
            {availableProviders.includes('falai') && <option value="falai">fal.ai (Chatterbox)</option>}
            {availableProviders.includes('elevenlabs') && <option value="elevenlabs">ElevenLabs</option>}
          </select>
        )}

        {voices.length > 1 && (
          <select
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-purple-500"
            disabled={generating}
          >
            <option value="">Select voice...</option>
            {voices.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        )}

        <button
          onClick={handleGenerate}
          disabled={!text.trim() || disabled || generating}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors ml-auto"
        >
          {generating ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Volume2 size={12} />
              Generate
            </>
          )}
        </button>
      </div>

      {generating && (
        <div className="bg-slate-900/50 rounded-lg p-2 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin text-amber-400" />
          <span className="text-[10px] text-slate-400">
            Generating voiceover with {provider === 'falai' ? 'fal.ai' : 'ElevenLabs'}... This may take up to 30 seconds.
          </span>
        </div>
      )}

      {error && (
        <p className="text-[10px] text-red-400">{error}</p>
      )}

      <p className="text-[10px] text-slate-600">
        {provider === 'falai'
          ? 'Powered by fal.ai Chatterbox. Supports emotion tags like [chuckle], [sigh].'
          : 'Powered by ElevenLabs. High-quality, natural-sounding voices.'}
      </p>
    </div>
  );
}
